export enum PermissionEnum {
  // Company
  COMPANY_SETTINGS_VIEW = 'COMPANY_SETTINGS_VIEW',
  COMPANY_SETTINGS_UPDATE = 'COMPANY_SETTINGS_UPDATE',
  COMPANY_PLATFORM_CHANGE = 'COMPANY_PLATFORM_CHANGE',
  COMPANY_LOGS_VIEW = 'COMPANY_LOGS_VIEW',

  // Roles
  ROLES_VIEW = 'ROLES_VIEW',
  ROLES_CREATE = 'ROLES_CREATE',
  ROLES_UPDATE = 'ROLES_UPDATE',
  ROLES_DELETE = 'ROLES_DELETE',

  // Billing
  BILLING_VIEW = 'BILLING_VIEW',
  BILLING_MANAGE = 'BILLING_MANAGE',

  // Users
  USERS_VIEW = 'USERS_VIEW',
  USERS_INVITE = 'USERS_INVITE',
  USERS_UPDATE = 'USERS_UPDATE',
  USERS_SYNC = 'USERS_SYNC',
  USERS_IMPORT = 'USERS_IMPORT',
  USERS_CHANGE_STATUS = 'USERS_CHANGE_STATUS',
  USERS_CHANGE_ROLE = 'USERS_CHANGE_ROLE',

  // Leave Requests
  LEAVE_REQUEST_CREATE = 'LEAVE_REQUEST_CREATE',
  LEAVE_REQUEST_UPDATE = 'LEAVE_REQUEST_UPDATE',
  LEAVE_REQUEST_CANCEL = 'LEAVE_REQUEST_CANCEL',
  LEAVE_REQUEST_APPROVE = 'LEAVE_REQUEST_APPROVE',
  LEAVE_REQUEST_ADD = 'LEAVE_REQUEST_ADD',
  LEAVE_REQUEST_DENY = 'LEAVE_REQUEST_DENY',
  LEAVE_REQUEST_DELETE = 'LEAVE_REQUEST_DELETE',

  // TOIL Requests
  TOIL_REQUEST_CREATE = 'TOIL_REQUEST_CREATE',
  TOIL_REQUEST_UPDATE = 'TOIL_REQUEST_UPDATE',
  TOIL_REQUEST_CANCEL = 'TOIL_REQUEST_CANCEL',
  TOIL_REQUEST_APPROVE = 'TOIL_REQUEST_APPROVE',
  TOIL_REQUEST_ADD = 'TOIL_REQUEST_ADD',
  TOIL_REQUEST_DENY = 'TOIL_REQUEST_DENY',
  TOIL_REQUEST_DELETE = 'TOIL_REQUEST_DELETE',

  // Locations
  LOCATIONS_VIEW = 'LOCATIONS_VIEW',
  LOCATIONS_CREATE = 'LOCATIONS_CREATE',
  LOCATIONS_UPDATE = 'LOCATIONS_UPDATE',
  LOCATIONS_DELETE = 'LOCATIONS_DELETE',
  LOCATION_HOLIDAYS_VIEW = 'LOCATION_HOLIDAYS_VIEW',
  LOCATIONS_HOLIDAYS_MANAGE = 'LOCATIONS_HOLIDAYS_MANAGE',

  // Departments
  DEPARTMENTS_VIEW = 'DEPARTMENTS_VIEW',
  DEPARTMENTS_CREATE = 'DEPARTMENTS_CREATE',
  DEPARTMENTS_UPDATE = 'DEPARTMENTS_UPDATE',
  DEPARTMENTS_DELETE = 'DEPARTMENTS_DELETE',
  DEPARTMENTS_APPROVERS_MANAGE = 'DEPARTMENTS_APPROVERS_MANAGE',

  // Leave Types & Policies
  LEAVE_TYPES_VIEW = 'LEAVE_TYPES_VIEW',
  LEAVE_TYPES_CREATE = 'LEAVE_TYPES_CREATE',
  LEAVE_TYPES_UPDATE = 'LEAVE_TYPES_UPDATE',
  LEAVE_TYPES_DELETE = 'LEAVE_TYPES_DELETE',
  LEAVE_POLICY_VIEW = 'LEAVE_POLICY_VIEW',
  LEAVE_POLICY_CREATE = 'LEAVE_POLICY_CREATE',
  LEAVE_POLICY_UPDATE = 'LEAVE_POLICY_UPDATE',
  LEAVE_POLICY_DELETE = 'LEAVE_POLICY_DELETE',

  // Labels
  LABELS_CREATE = 'LABELS_CREATE',
  LABELS_UPDATE = 'LABELS_UPDATE',
  LABELS_DELETE = 'LABELS_DELETE',
  LABELS_ASSIGN = 'LABELS_ASSIGN',

  // Notifications
  NOTIFICATIONS_CREATE = 'NOTIFICATIONS_CREATE',
  NOTIFICATIONS_UPDATE = 'NOTIFICATIONS_UPDATE',
  NOTIFICATIONS_DELETE = 'NOTIFICATIONS_DELETE',

  // Reports
  REPORTS_VIEW = 'REPORTS_VIEW',
  REPORTS_EXPORT = 'REPORTS_EXPORT',
  SCHEDULED_REPORTS_CREATE = 'SCHEDULED_REPORTS_CREATE',
  SCHEDULED_REPORTS_UPDATE = 'SCHEDULED_REPORTS_UPDATE',
  SCHEDULED_REPORTS_DELETE = 'SCHEDULED_REPORTS_DELETE',

  // Addons
  ADDONS_VIEW = 'ADDONS_VIEW',
  ADDONS_CREATE = 'ADDONS_CREATE',
  ADDONS_UPDATE = 'ADDONS_UPDATE',
  ADDONS_DELETE = 'ADDONS_DELETE',
  ADDONS_ACTIVATE = 'ADDONS_ACTIVATE',
  ADDONS_DEACTIVATE = 'ADDONS_DEACTIVATE',

  // Calendar
  CALENDAR_VIEW = 'CALENDAR_VIEW',

  // Wallchart
  WALLCHART_VIEW = 'WALLCHART_VIEW',

  // Dashboard
  DASHBOARD_VIEW = 'DASHBOARD_VIEW',

  // Import
  IMPORT_LEAVES = 'IMPORT_LEAVES',
  IMPORT_TOILS = 'IMPORT_TOILS',
  IMPORT_BROUGHT_FORWARD = 'IMPORT_BROUGHT_FORWARD',
  IMPORT_USERS_DATA = 'IMPORT_USERS_DATA',

  // Energy
  ENERGY_SCORE_VIEW = 'ENERGY_SCORE_VIEW',

  // Quarterly Insights
  QUARTERLY_INSIGHTS_VIEW = 'QUARTERLY_INSIGHTS_VIEW',
  QUARTERLY_INSIGHTS_REPORT = 'QUARTERLY_INSIGHTS_REPORT',
}

export type Permission = `${PermissionEnum}`

export enum RoleScopeTypeEnum {
  COMPANY = 'COMPANY',
  LOCATION = 'LOCATION',
  DEPARTMENT = 'DEPARTMENT',
  SELF = 'SELF',
}

export type RoleScopeType = `${RoleScopeTypeEnum}`

export interface IRoleScope {
  type: RoleScopeType
  resourceIds: string[]
}

export enum DefaultRoleIdEnum {
  ADMIN = 'ADMIN',
  LOCATION_ADMIN = 'LOCATION_ADMIN',
  APPROVER = 'APPROVER',
  USER = 'USER',
}

export type DefaultRoleId = `${DefaultRoleIdEnum}`

export interface IRole {
  id: DefaultRoleId | (string & {}) // custom role
  name: string
  description: string
  permissions: Permission[]
  scopeType: RoleScopeType
  isDefault: boolean
  isEditable: boolean
}

export interface IRoleAssignment {
  userId: string
  roleId: string
  scope: IRoleScope
}

export interface IPermissionCheckParams {
  userId: string
  permission: Permission
  resourceType?: RoleScopeType
  resourceId?: string
}

export const ADMIN_PERMISSIONS: Permission[] = Object.values(PermissionEnum)

export const LOCATION_ADMIN_PERMISSIONS: Permission[] = [
  PermissionEnum.DASHBOARD_VIEW,
  PermissionEnum.CALENDAR_VIEW,
  PermissionEnum.WALLCHART_VIEW,

  PermissionEnum.USERS_UPDATE,
  PermissionEnum.USERS_VIEW,
  PermissionEnum.USERS_INVITE,
  PermissionEnum.USERS_CHANGE_STATUS,
  PermissionEnum.USERS_CHANGE_ROLE,

  PermissionEnum.LEAVE_TYPES_VIEW,
  PermissionEnum.LEAVE_POLICY_VIEW,
  PermissionEnum.LEAVE_POLICY_UPDATE,
  PermissionEnum.LEAVE_POLICY_DELETE,

  PermissionEnum.LEAVE_REQUEST_CREATE,
  PermissionEnum.LEAVE_REQUEST_ADD,
  PermissionEnum.LEAVE_REQUEST_UPDATE,
  PermissionEnum.LEAVE_REQUEST_CANCEL,
  PermissionEnum.LEAVE_REQUEST_APPROVE,
  PermissionEnum.LEAVE_REQUEST_DENY,
  PermissionEnum.LEAVE_REQUEST_DELETE,

  PermissionEnum.TOIL_REQUEST_CREATE,
  PermissionEnum.TOIL_REQUEST_ADD,
  PermissionEnum.TOIL_REQUEST_UPDATE,
  PermissionEnum.TOIL_REQUEST_CANCEL,
  PermissionEnum.TOIL_REQUEST_APPROVE,
  PermissionEnum.TOIL_REQUEST_DENY,
  PermissionEnum.TOIL_REQUEST_DELETE,

  PermissionEnum.LOCATIONS_VIEW,
  PermissionEnum.LOCATIONS_UPDATE,
  PermissionEnum.LOCATIONS_HOLIDAYS_MANAGE,
  PermissionEnum.LOCATION_HOLIDAYS_VIEW,

  PermissionEnum.DEPARTMENTS_VIEW,
  PermissionEnum.DEPARTMENTS_CREATE,
  PermissionEnum.DEPARTMENTS_UPDATE,
  PermissionEnum.DEPARTMENTS_DELETE,
  PermissionEnum.DEPARTMENTS_APPROVERS_MANAGE,

  PermissionEnum.NOTIFICATIONS_CREATE,
  PermissionEnum.NOTIFICATIONS_UPDATE,
  PermissionEnum.NOTIFICATIONS_DELETE,

  PermissionEnum.REPORTS_VIEW,
  PermissionEnum.REPORTS_EXPORT,
  PermissionEnum.SCHEDULED_REPORTS_CREATE,
  PermissionEnum.SCHEDULED_REPORTS_UPDATE,
  PermissionEnum.SCHEDULED_REPORTS_DELETE,
]

export const APPROVER_PERMISSIONS: Permission[] = [
  PermissionEnum.DASHBOARD_VIEW,
  PermissionEnum.CALENDAR_VIEW,
  PermissionEnum.WALLCHART_VIEW,

  PermissionEnum.USERS_VIEW,

  PermissionEnum.LEAVE_REQUEST_CREATE,
  PermissionEnum.LEAVE_REQUEST_UPDATE,
  PermissionEnum.LEAVE_REQUEST_CANCEL,
  PermissionEnum.LEAVE_REQUEST_APPROVE,
  PermissionEnum.LEAVE_REQUEST_DENY,

  PermissionEnum.TOIL_REQUEST_CREATE,
  PermissionEnum.TOIL_REQUEST_UPDATE,
  PermissionEnum.TOIL_REQUEST_CANCEL,
  PermissionEnum.TOIL_REQUEST_APPROVE,
  PermissionEnum.TOIL_REQUEST_DENY,
]

export const USER_PERMISSIONS: Permission[] = [
  PermissionEnum.DASHBOARD_VIEW,
  PermissionEnum.CALENDAR_VIEW,

  PermissionEnum.USERS_VIEW,

  PermissionEnum.LOCATION_HOLIDAYS_VIEW,

  PermissionEnum.LEAVE_REQUEST_CREATE,
  PermissionEnum.LEAVE_REQUEST_UPDATE,
  PermissionEnum.LEAVE_REQUEST_CANCEL,

  PermissionEnum.TOIL_REQUEST_CREATE,
  PermissionEnum.TOIL_REQUEST_UPDATE,
  PermissionEnum.TOIL_REQUEST_CANCEL,
]

export const DEFAULT_ROLE_PERMISSIONS: Record<DefaultRoleId, Permission[]> = {
  [DefaultRoleIdEnum.ADMIN]: ADMIN_PERMISSIONS,
  [DefaultRoleIdEnum.LOCATION_ADMIN]: LOCATION_ADMIN_PERMISSIONS,
  [DefaultRoleIdEnum.APPROVER]: APPROVER_PERMISSIONS,
  [DefaultRoleIdEnum.USER]: USER_PERMISSIONS,
}

export const DEFAULT_ROLE_SCOPE_TYPES: Record<DefaultRoleId, RoleScopeType> = {
  [DefaultRoleIdEnum.ADMIN]: RoleScopeTypeEnum.COMPANY,
  [DefaultRoleIdEnum.LOCATION_ADMIN]: RoleScopeTypeEnum.LOCATION,
  [DefaultRoleIdEnum.APPROVER]: RoleScopeTypeEnum.DEPARTMENT,
  [DefaultRoleIdEnum.USER]: RoleScopeTypeEnum.SELF,
}
